import { useState, useCallback, useEffect, useRef } from "react";
import { isInteractiveSelectionTarget } from "../utils/selectionUtils";

const AUTO_SCROLL_MAX_SPEED = 15; // Max scroll speed while dragging past an edge (px per frame)

const clipRectToBounds = (rect, bounds) => {
  const left = Math.max(rect.left, bounds.left);
  const top = Math.max(rect.top, bounds.top);
  const right = Math.min(rect.right, bounds.right);
  const bottom = Math.min(rect.bottom, bounds.bottom);
  if (right <= left || bottom <= top) return null;
  return { left, top, right, bottom, width: right - left, height: bottom - top };
};

export function useRepoMarqueeSelection(filteredItems, setSelectedItems) {
  const containerRef = useRef(null);
  const selectionStartRef = useRef({ x: 0, y: 0 });
  const selectionActiveRef = useRef(false);
  const dragSelectionRef = useRef(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  // Where the marquee started, in the scroll container's CONTENT coordinates.
  // Anchoring here (instead of the viewport) keeps items selected as the list
  // scrolls underneath the box during a drag.
  const startContentRef = useRef({ x: 0, y: 0 });
  const scrollVelocityRef = useRef({ x: 0, y: 0 });
  const rafIdRef = useRef(null);
  const [selectionRect, setSelectionRect] = useState(null);

  // Keep the latest filtered items in a ref so the mousemove/mouseup
  // listeners never re-attach (and never cancel the auto-scroll loop)
  // just because the parent re-rendered mid-drag.
  const filteredItemsRef = useRef(filteredItems);
  useEffect(() => {
    filteredItemsRef.current = filteredItems;
  }, [filteredItems]);

  // The element that actually scrolls the item list (grid view: .repo-grid,
  // list view: .repo-table-inner). Only its visible box is a valid target for
  // marquee selection — items scrolled out of it are clipped/hidden.
  const getScrollContainer = useCallback(() => {
    if (!containerRef.current) return null;
    return (
      containerRef.current.querySelector(".repo-grid, .repo-table-inner") ||
      null
    );
  }, []);

  const getScrollMetrics = useCallback(() => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer) return null;
    return {
      bounds: scrollContainer.getBoundingClientRect(),
      scrollLeft: scrollContainer.scrollLeft,
      scrollTop: scrollContainer.scrollTop,
    };
  }, [getScrollContainer]);

  const stopAutoScroll = useCallback(() => {
    scrollVelocityRef.current = { x: 0, y: 0 };
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  // Hit-test in content space: each item's rect is shifted by the current
  // scroll, so items that scrolled out of view keep their position relative
  // to where the drag started and remain selected.
  const selectItemsInRect = useCallback(
    (contentRect) => {
      if (!containerRef.current || !contentRect) {
        setSelectedItems(new Set());
        return;
      }
      const metrics = getScrollMetrics();
      const containerRect = containerRef.current.getBoundingClientRect();
      const bounds = metrics ? metrics.bounds : containerRect;
      const scrollLeft = metrics ? metrics.scrollLeft : 0;
      const scrollTop = metrics ? metrics.scrollTop : 0;

      const selected = new Set();
      containerRef.current
        .querySelectorAll("[data-item-id]")
        .forEach((node) => {
          const itemRect = node.getBoundingClientRect();
          const itemLeft = itemRect.left - bounds.left + scrollLeft;
          const itemRight = itemRect.right - bounds.left + scrollLeft;
          const itemTop = itemRect.top - bounds.top + scrollTop;
          const itemBottom = itemRect.bottom - bounds.top + scrollTop;
          const isIntersecting =
            itemRight >= contentRect.left &&
            itemLeft <= contentRect.right &&
            itemBottom >= contentRect.top &&
            itemTop <= contentRect.bottom;
          if (isIntersecting) {
            const idStr = node.dataset.itemId;
            const originalItem = filteredItemsRef.current.find(
              (item) => String(item.id) === idStr,
            );
            if (originalItem) {
              selected.add(originalItem.id);
            } else {
              selected.add(idStr);
            }
          }
        });
      setSelectedItems(selected);
    },
    [getScrollMetrics, setSelectedItems],
  );

  // Computes both versions of the marquee rectangle:
  //  - selectRect:  content-space rect from the drag start to the cursor,
  //    used for hit-testing (so scrolled items stay selected)
  //  - renderRect:  container-space rect for the absolutely-positioned box,
  //    clipped to the visible area so it never paints over the header
  const computeSelectionRects = useCallback(
    (startContent, clientX, clientY) => {
      if (!containerRef.current) return { selectRect: null, renderRect: null };

      const metrics = getScrollMetrics();
      const containerRect = containerRef.current.getBoundingClientRect();
      const bounds = metrics ? metrics.bounds : containerRect;
      const scrollLeft = metrics ? metrics.scrollLeft : 0;
      const scrollTop = metrics ? metrics.scrollTop : 0;

      const currentX = clientX - bounds.left + scrollLeft;
      const currentY = clientY - bounds.top + scrollTop;

      const selectRect = {
        left: Math.min(startContent.x, currentX),
        top: Math.min(startContent.y, currentY),
        right: Math.max(startContent.x, currentX),
        bottom: Math.max(startContent.y, currentY),
      };

      // A zero-size marquee (press without moving) selects nothing.
      if (
        selectRect.right <= selectRect.left ||
        selectRect.bottom <= selectRect.top
      ) {
        return { selectRect: null, renderRect: null };
      }

      // Convert to viewport space for rendering, then clip to the visible box.
      const renderViewport = clipRectToBounds(
        {
          left: selectRect.left - scrollLeft + bounds.left,
          top: selectRect.top - scrollTop + bounds.top,
          right: selectRect.right - scrollLeft + bounds.left,
          bottom: selectRect.bottom - scrollTop + bounds.top,
        },
        bounds,
      );

      return {
        selectRect,
        renderRect: renderViewport
          ? {
              left: renderViewport.left - containerRect.left,
              top: renderViewport.top - containerRect.top,
              width: renderViewport.width,
              height: renderViewport.height,
            }
          : null,
      };
    },
    [getScrollMetrics],
  );

  const updateSelection = useCallback(
    (clientX, clientY) => {
      if (!selectionActiveRef.current || !containerRef.current) return;
      const { selectRect, renderRect } = computeSelectionRects(
        startContentRef.current,
        clientX,
        clientY,
      );
      setSelectionRect(renderRect);
      selectItemsInRect(selectRect);
    },
    [computeSelectionRects, selectItemsInRect],
  );

  const autoScrollTick = useCallback(() => {
    const scrollContainer = getScrollContainer();
    if (!selectionActiveRef.current || !scrollContainer) {
      stopAutoScroll();
      return;
    }

    const maxScrollTop =
      scrollContainer.scrollHeight - scrollContainer.clientHeight;
    const maxScrollLeft =
      scrollContainer.scrollWidth - scrollContainer.clientWidth;
    let { x, y } = scrollVelocityRef.current;

    if (y > 0 && scrollContainer.scrollTop >= maxScrollTop) y = 0;
    if (y < 0 && scrollContainer.scrollTop <= 0) y = 0;
    if (x > 0 && scrollContainer.scrollLeft >= maxScrollLeft) x = 0;
    if (x < 0 && scrollContainer.scrollLeft <= 0) x = 0;

    if (!x && !y) {
      stopAutoScroll();
      return;
    }

    scrollContainer.scrollTop += y;
    scrollContainer.scrollLeft += x;
    if (lastPointerRef.current) {
      updateSelection(lastPointerRef.current.x, lastPointerRef.current.y);
    }
    rafIdRef.current = requestAnimationFrame(autoScrollTick);
  }, [getScrollContainer, stopAutoScroll, updateSelection]);

  const updateAutoScroll = useCallback(
    (clientX, clientY) => {
      const scrollContainer = getScrollContainer();
      if (!selectionActiveRef.current || !scrollContainer) {
        stopAutoScroll();
        return;
      }

      // Only auto-scroll once the user is actually dragging (past the 5px
      // threshold). Otherwise just starting a box at the top of the list
      // immediately scrolls the table up under the cursor.
      if (!dragSelectionRef.current) {
        stopAutoScroll();
        return;
      }

      const bounds = scrollContainer.getBoundingClientRect();
      const maxScrollTop =
        scrollContainer.scrollHeight - scrollContainer.clientHeight;
      const maxScrollLeft =
        scrollContainer.scrollWidth - scrollContainer.clientWidth;

      let velocityX = 0;
      let velocityY = 0;

      // Scroll only when the pointer moves PAST the visible edge (not merely
      // near it), so beginning a marquee at the top of the list never scrolls
      // it up. Speed scales with how far past the edge the pointer is.
      if (clientY < bounds.top && scrollContainer.scrollTop > 0) {
        velocityY = -Math.min(AUTO_SCROLL_MAX_SPEED, bounds.top - clientY);
      } else if (
        clientY > bounds.bottom &&
        scrollContainer.scrollTop < maxScrollTop
      ) {
        velocityY = Math.min(AUTO_SCROLL_MAX_SPEED, clientY - bounds.bottom);
      }

      if (clientX < bounds.left && scrollContainer.scrollLeft > 0) {
        velocityX = -Math.min(AUTO_SCROLL_MAX_SPEED, bounds.left - clientX);
      } else if (
        clientX > bounds.right &&
        scrollContainer.scrollLeft < maxScrollLeft
      ) {
        velocityX = Math.min(AUTO_SCROLL_MAX_SPEED, clientX - bounds.right);
      }

      scrollVelocityRef.current = { x: velocityX, y: velocityY };

      if (velocityX || velocityY) {
        if (!rafIdRef.current) {
          rafIdRef.current = requestAnimationFrame(autoScrollTick);
        }
      } else {
        stopAutoScroll();
      }
    },
    [autoScrollTick, getScrollContainer, stopAutoScroll],
  );

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    if (!containerRef.current || !containerRef.current.contains(event.target))
      return;
    if (isInteractiveSelectionTarget(event.target)) return;

    const targetCard = event.target.closest(".repo-card, .repo-list-row");
    if (targetCard) {
      return;
    }

    selectionActiveRef.current = true;
    dragSelectionRef.current = false;
    selectionStartRef.current = { x: event.clientX, y: event.clientY };
    lastPointerRef.current = { x: event.clientX, y: event.clientY };

    // Anchor the drag start in content coordinates. The start point is
    // clamped to the visible box so starting above the list (e.g. in the
    // header/filter area) still begins the selection at the list's top
    // without selecting hidden items.
    const metrics = getScrollMetrics();
    if (metrics) {
      const { bounds, scrollLeft, scrollTop } = metrics;
      const startX = Math.min(Math.max(event.clientX, bounds.left), bounds.right);
      const startY = Math.min(Math.max(event.clientY, bounds.top), bounds.bottom);
      startContentRef.current = {
        x: startX - bounds.left + scrollLeft,
        y: startY - bounds.top + scrollTop,
      };
    } else {
      startContentRef.current = { x: event.clientX, y: event.clientY };
    }

    updateSelection(event.clientX, event.clientY);
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!selectionActiveRef.current) return;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      updateSelection(event.clientX, event.clientY);
      if (
        Math.abs(event.clientX - selectionStartRef.current.x) > 5 ||
        Math.abs(event.clientY - selectionStartRef.current.y) > 5
      ) {
        dragSelectionRef.current = true;
      }
      updateAutoScroll(event.clientX, event.clientY);
    };

    const handleMouseUp = (event) => {
      if (!selectionActiveRef.current) return;
      updateSelection(event.clientX, event.clientY);
      selectionActiveRef.current = false;
      setSelectionRect(null);
      stopAutoScroll();
      window.requestAnimationFrame(() => {
        dragSelectionRef.current = false;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      stopAutoScroll();
    };
  }, [updateSelection, updateAutoScroll, stopAutoScroll]);

  return {
    containerRef,
    dragSelectionRef,
    selectionRect,
    handleMouseDown,
  };
}
